import React, { useState, useRef, useEffect } from 'react';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Tooltip from '@material-ui/core/Tooltip';
import HelpIcon from '@material-ui/icons/Help';
import { makeStyles } from '@material-ui/core/styles';
import Layout from '../Layout';
import ForecastSettingsSection from './ForecastSettingsSection';
import ResultsSection from './ResultsSection';
import { useMonteCarloSimulation } from './useMonteCarloSimulation';
import { ForecastType, MetricType, SimulationResult } from './types';
import { TEST_DATA } from './constants';

const useStyles = makeStyles(theme => ({
  helpIcon: {
    fontSize: '22px',
    marginLeft: theme.spacing(1),
  },
  labelWithHelp: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
}));

const CrystalBall: React.FC = () => {
  const classes = useStyles();
  const [throughputData, setThroughputData] = useState('');
  const [metricType, setMetricType] = useState<MetricType>('throughput');
  const [forecastType, setForecastType] = useState<ForecastType>('items-in-sprints');
  const [numberOfSprints, setNumberOfSprints] = useState('3');
  const [numberOfItems, setNumberOfItems] = useState('10');
  const [results, setResults] = useState<SimulationResult | null>(null);
  
  const { isCalculating, runSimulation } = useMonteCarloSimulation();
  const resultsRef = useRef<HTMLDivElement>(null);

  // Reset results whenever parameters change
  useEffect(() => {
    setResults(null);
  }, [throughputData, forecastType, numberOfSprints, numberOfItems]);

  const handleMetricTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMetricType(event.target.value as MetricType);
  };

  const handleForecastTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setForecastType(event.target.value as ForecastType);
  };

  const handlePopulateTestData = () => {
    setThroughputData(TEST_DATA[metricType]);
  };

  const handleGenerateForecast = async () => {
    try {
      const targetSprints = parseInt(numberOfSprints);
      const targetItems = parseInt(numberOfItems);
      
      const simulationResult = await runSimulation(
        throughputData,
        targetSprints,
        targetItems,
        forecastType
      );
      
      setResults(simulationResult);
      
      // Auto-scroll to results section after simulation completes
      setTimeout(() => {
        if (resultsRef.current) {
          resultsRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }
      }, 100);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'An error occurred');
    }
  };

  return (
    <Layout>
      <Grid container direction="row" justifyContent="center">
        <Grid item xs={12}>
          <Box my={4}>
            <Typography variant="h3" align="center">
              Crystal Ball
            </Typography>
            <div className={classes.labelWithHelp}>
              <Typography variant="body1" align="center">Averages suck. Use <strong>Monte Carlo</strong> method to predict the future based on your historical velocity or throughput metrics</Typography>
              <Tooltip 
                placement="right" 
                title={
                  <Typography variant="body1">
                    <div><strong>Why averages fail:</strong> Simple averages ignore variability and uncertainty. A team averaging 5 items/sprint could deliver 2 or 8 items - averages hide this critical information.</div>
                    <br />
                    <div><strong>Monte Carlo simulation:</strong> Runs thousands of scenarios using your historical data's actual distribution. It samples random values from your past performance to simulate realistic future outcomes, giving you probability ranges instead of misleading single numbers.</div>
                  </Typography>
                }
              >
                <HelpIcon className={classes.helpIcon} color='primary' />
              </Tooltip>
            </div>
          </Box>
        </Grid>
        
        {/* Forecast Settings Section */}
        <Grid item xs={12} md={4}>
          <Box m={2}>
            <ForecastSettingsSection
              metricType={metricType}
              throughputData={throughputData}
              onMetricTypeChange={handleMetricTypeChange}
              onThroughputDataChange={setThroughputData}
              onPopulateTestData={handlePopulateTestData}
              forecastType={forecastType}
              numberOfSprints={numberOfSprints}
              numberOfItems={numberOfItems}
              isCalculating={isCalculating}
              onForecastTypeChange={handleForecastTypeChange}
              onNumberOfSprintsChange={setNumberOfSprints}
              onNumberOfItemsChange={setNumberOfItems}
            />
          </Box>
        </Grid>
            
        {/* Results Section */}
        <Grid item xs={12} md={4}>
          <div ref={resultsRef}>
            <Box m={2}>
              <ResultsSection
                results={results}
                forecastType={forecastType}
                metricType={metricType}
                numberOfSprints={numberOfSprints}
                numberOfItems={numberOfItems}
                isCalculating={isCalculating}
                onGenerateForecast={handleGenerateForecast}
              />
            </Box>
          </div>
        </Grid>
      </Grid>
    </Layout>
  );
};

export default CrystalBall;